import HttpService from './HttpService';

const API_URL = '/house-floor-plans';

/**
 * Format error for consistent handling across components
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} - Structured error with message and code
 */
const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.error || error.response?.data?.message || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.errors || error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details };
};

/**
 * Service for merged house floor plans (ADR 0007) — live compositions of
 * per-room plans. The composed GET serves the same viewer-ready `data`
 * blob as room scans and user floor plans, with placement transforms
 * already pre-multiplied into every surface.
 */
const HouseFloorPlanService = {
  /**
   * List a house's floor plans (lightweight cards, no geometry).
   * @param {number} houseId
   * @returns {Promise<{floor_plans: Array, total: number}>}
   */
  getFloorPlansForHouse: async (houseId) => {
    try {
      const response = await HttpService.get(`${API_URL}/house/${houseId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching floor plans for house ${houseId}`);
    }
  },

  /**
   * Get one floor plan, fully composed for the viewer.
   * @param {number} planId
   * @returns {Promise<Object>} - {data, placements, floor_area, floor_sections, ...}
   */
  getFloorPlan: async (planId) => {
    try {
      const response = await HttpService.get(`${API_URL}/${planId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching floor plan ${planId}`);
    }
  },

  /**
   * Create an empty floor plan for one floor of a house.
   * @param {Object} data - {house_id, floor_label}
   * @returns {Promise<Object>} - Composed floor plan (empty placements)
   */
  createFloorPlan: async (data) => {
    try {
      const response = await HttpService.post(API_URL, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating floor plan');
    }
  },

  /**
   * Rename the floor label.
   * @param {number} planId
   * @param {Object} data - {floor_label}
   * @returns {Promise<Object>} - Floor plan summary
   */
  updateFloorPlan: async (planId, data) => {
    try {
      const response = await HttpService.patch(`${API_URL}/${planId}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating floor plan ${planId}`);
    }
  },

  /**
   * Delete a floor plan. Placements go with it; Room Plans are untouched.
   * @param {number} planId
   */
  deleteFloorPlan: async (planId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${planId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting floor plan ${planId}`);
    }
  },

  /**
   * Place a room into the floor. Body comes from `buildPlacementPayload`
   * in utils/pairingSolver.js (the first room may omit `pairing`).
   * @param {number} planId
   * @param {Object} payload - {plan_id, transform, pairing?, sort_order}
   * @returns {Promise<Object>} - Refreshed composed floor plan
   */
  addPlacement: async (planId, payload) => {
    try {
      const response = await HttpService.post(`${API_URL}/${planId}/placements`, payload);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error placing room in floor plan');
    }
  },

  /**
   * Re-align (fresh transform), re-pair, or reorder a placement.
   * @param {number} planId
   * @param {number} placementId
   * @param {Object} payload - {transform?, pairing?, sort_order?}
   * @returns {Promise<Object>} - Refreshed composed floor plan
   */
  updatePlacement: async (planId, placementId, payload) => {
    try {
      const response = await HttpService.patch(`${API_URL}/${planId}/placements/${placementId}`, payload);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error updating placement');
    }
  },

  /**
   * Remove a room from the floor. Its Room Plan is untouched.
   * @param {number} planId
   * @param {number} placementId
   */
  removePlacement: async (planId, placementId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${planId}/placements/${placementId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error removing placement');
    }
  },
};

export default HouseFloorPlanService;
